/**
 * GST calculation (India) and amount in words
 * Same state → CGST + SGST; Different state → IGST. Default rate 18%.
 */

export function buildProjectLineItems(project) {
  const services = project?.services?.length ? project.services : [{ service: 'Production', amount: project?.projectValue || 0 }];
  return services.map((s) => {
    const particulars = typeof s === 'string' ? s : s.service;
    const price = typeof s === 'string' 
      ? ((Number(project?.projectValue) || 0) / services.length) 
      : (Number(s.amount) || 0);
    return {
      particulars: particulars || 'Service',
      price: price,
      qty: 1,
      total: price,
      fromProject: true,
    };
  });
}

export function rowTotal(row) {
  return (Number(row.price) || 0) * (Number(row.qty) || 1);
}

export function sumLineItems(rows) {
  return (rows || []).reduce((sum, row) => sum + rowTotal(row), 0);
}

export function isSameStateGst(companyState, clientState) {
  const company = (companyState || '').trim().toLowerCase();
  const client = (clientState || '').trim().toLowerCase();
  if (!company) return true;
  if (!client) return true;
  return company === client;
}

export function computeInvoiceTotals(project, additionalRows = [], settings = {}) {
  const baseRows = buildProjectLineItems(project);
  const extraRows = (additionalRows || []).filter((r) => r.particulars?.trim());
  const lineItems = [...baseRows, ...extraRows.map((row) => ({
    particulars: row.particulars,
    price: Number(row.price) || 0,
    qty: Number(row.qty) || 1,
    total: rowTotal(row),
  }))];
  const subtotal = sumLineItems(lineItems);
  const gstRate = Number(settings.gstRate) || 18;
  const sameState = isSameStateGst(settings.companyState, project?.clientState);
  const tax = getGstBreakdown(subtotal, gstRate, sameState);
  const grandTotal = subtotal + tax.totalTax;
  const additionalAmount = sumLineItems(extraRows);
  return { lineItems, subtotal, additionalAmount, ...tax, grandTotal, gstRate, sameState };
}

export function getGstBreakdown(amount, gstRatePercent, isSameState) {
  const rate = (gstRatePercent || 18) / 100;
  const taxAmount = Math.round(amount * rate * 100) / 100;
  if (isSameState) {
    const half = Math.round(taxAmount * 50) / 100;
    return {
      cgst: half,
      sgst: half,
      igst: 0,
      totalTax: taxAmount,
      type: 'CGST+SGST',
    };
  }
  return {
    cgst: 0,
    sgst: 0,
    igst: taxAmount,
    totalTax: taxAmount,
    type: 'IGST',
  };
}

const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];

function twoDigits(n) {
  if (n < 10) return ones[n];
  if (n < 20) return teens[n - 10];
  const t = Math.floor(n / 10);
  const o = n % 10;
  return tens[t] + (o ? ' ' + ones[o] : '');
}

function threeDigits(n) {
  if (n === 0) return '';
  const h = Math.floor(n / 100);
  const rest = n % 100;
  const part = h ? ones[h] + ' Hundred' + (rest ? ' ' : '') : '';
  return part + twoDigits(rest);
}

/**
 * Convert number to Indian Rupee words (e.g. 12345.67 → "Twelve Thousand Three Hundred Forty Five Rupees And Sixty Seven Paise Only")
 */
export function amountInWords(num) {
  const n = Math.floor(num);
  const paise = Math.round((num - n) * 100);
  if (n === 0 && paise === 0) return 'Zero Rupees Only';
  const crore = Math.floor(n / 1e7);
  const lakh = Math.floor((n % 1e7) / 1e5);
  const thousand = Math.floor((n % 1e5) / 1e3);
  const rest = n % 1000;
  let s = '';
  if (crore) s += threeDigits(crore) + ' Crore ';
  if (lakh) s += threeDigits(lakh) + ' Lakh ';
  if (thousand) s += threeDigits(thousand) + ' Thousand ';
  if (rest) s += threeDigits(rest);
  s = s.trim();
  s = s ? s + ' Rupees' : '';
  if (paise) s += (s ? ' And ' : '') + twoDigits(paise) + ' Paise';
  return (s || 'Zero Rupees') + ' Only';
}
